import { IUser, AuthResponse, AuthSessionInfo } from '../types/damTypes';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api';

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_SESSION_KEY = 'authSession';
const AUTH_LOGOUT_REASON_KEY = 'authLogoutReason';
const AUTH_ACTIVITY_KEY = 'authLastActivityAt';
const AUTH_EVENT_KEY = 'authEvent';
const AUTH_EVENT_NAME = 'dam:auth-event';
const ACTIVITY_BROADCAST_THROTTLE_MS = 30 * 1000;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

export type LogoutReason = 'idle_timeout' | 'password_changed' | 'session_invalid' | 'manual_logout' | string;

export interface ApiResponse<T> {
  data: T | null;
  error?: string;
  message?: string;
  status?: number;
  code?: string;
}

export interface AuthEventPayload {
  type: 'login' | 'logout' | 'session_updated';
  at: number;
  reason?: LogoutReason;
}

export interface AuthenticatedUserResponse {
  user: IUser;
  session: AuthSessionInfo;
}

export interface SessionHeartbeatResponse {
  message: string;
  session: AuthSessionInfo;
}

interface AuthFailureResponse {
  code?: string;
  message?: string;
}

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function removeLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function dispatchAuthEvent(payload: AuthEventPayload): void {
  writeLocalStorage(AUTH_EVENT_KEY, JSON.stringify(payload));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<AuthEventPayload>(AUTH_EVENT_NAME, { detail: payload }));
  }
}

function mapAuthFailureToLogoutReason(code?: string): LogoutReason | undefined {
  if (code === 'AUTH_SESSION_IDLE_TIMEOUT') return 'idle_timeout';
  if (code && code.startsWith('AUTH_')) return 'session_invalid';
  return undefined;
}

function saveLogoutReason(reason?: LogoutReason): void {
  if (!reason) {
    removeLocalStorage(AUTH_LOGOUT_REASON_KEY);
    return;
  }

  writeLocalStorage(AUTH_LOGOUT_REASON_KEY, reason);
}

export function consumeLogoutReason(): LogoutReason | null {
  const reason = readLocalStorage(AUTH_LOGOUT_REASON_KEY);
  removeLocalStorage(AUTH_LOGOUT_REASON_KEY);
  return reason;
}

export function getStoredAuthSession(): AuthSessionInfo | null {
  const raw = readLocalStorage(AUTH_SESSION_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as AuthSessionInfo;
    if (!parsed || typeof parsed.sessionId !== 'string' || typeof parsed.idleTimeoutMs !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistSession(session: AuthSessionInfo | null): void {
  if (!session) {
    removeLocalStorage(AUTH_SESSION_KEY);
    return;
  }

  writeLocalStorage(AUTH_SESSION_KEY, JSON.stringify(session));
}

function persistAuthState(token: string, session: AuthSessionInfo | null): void {
  writeLocalStorage(AUTH_TOKEN_KEY, token);
  persistSession(session);
  recordUserActivity(Date.now(), true);
  saveLogoutReason(undefined);
  dispatchAuthEvent({ type: 'login', at: Date.now() });
}

function clearAuthState(reason?: LogoutReason): void {
  removeLocalStorage(AUTH_TOKEN_KEY);
  persistSession(null);
  removeLocalStorage(AUTH_ACTIVITY_KEY);
  saveLogoutReason(reason);
  dispatchAuthEvent({ type: 'logout', at: Date.now(), reason });
}

export function recordUserActivity(at = Date.now(), force = false): number {
  const previous = getLastActivityAt(at);
  if (!force && at - previous < ACTIVITY_BROADCAST_THROTTLE_MS) {
    return previous;
  }

  writeLocalStorage(AUTH_ACTIVITY_KEY, String(at));
  return at;
}

export function getLastActivityAt(defaultValue = Date.now()): number {
  const raw = readLocalStorage(AUTH_ACTIVITY_KEY);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }

  const session = getStoredAuthSession();
  if (!session) return defaultValue;

  const fromSession = Date.parse(session.lastActivityAt);
  return Number.isFinite(fromSession) ? fromSession : defaultValue;
}

export function getSessionTiming(now = Date.now()): {
  session: AuthSessionInfo | null;
  lastActivityAt: number;
  idleExpiresAt: number | null;
  expiredReason: 'idle_timeout' | null;
} {
  const session = getStoredAuthSession();
  const lastActivityAt = getLastActivityAt(now);

  if (!session) {
    return {
      session: null,
      lastActivityAt,
      idleExpiresAt: null,
      expiredReason: null,
    };
  }

  const idleExpiresAt = lastActivityAt + session.idleTimeoutMs;
  return {
    session,
    lastActivityAt,
    idleExpiresAt,
    expiredReason: now >= idleExpiresAt ? 'idle_timeout' : null,
  };
}

export function getHeartbeatIntervalMs(): number {
  return HEARTBEAT_INTERVAL_MS;
}

export function addAuthEventListener(listener: (payload: AuthEventPayload) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<AuthEventPayload>;
    if (customEvent.detail) {
      listener(customEvent.detail);
    }
  };

  window.addEventListener(AUTH_EVENT_NAME, handler as EventListener);
  return () => window.removeEventListener(AUTH_EVENT_NAME, handler as EventListener);
}

export const getAuthToken = (): string | null => {
  return readLocalStorage(AUTH_TOKEN_KEY);
};

export const setAuthToken = (token: string | null, session?: AuthSessionInfo | null): void => {
  if (!token) {
    clearAuthState();
    return;
  }

  persistAuthState(token, session || null);
};

export const isLoggedIn = (): boolean => !!getAuthToken();

export const verifyTokenStorage = (): boolean => {
  try {
    const originalToken = readLocalStorage(AUTH_TOKEN_KEY);
    const testKey = `_test_auth_${Date.now()}`;
    writeLocalStorage(testKey, 'test');
    const testValue = readLocalStorage(testKey);
    removeLocalStorage(testKey);

    if (testValue !== 'test') {
      return false;
    }

    if (originalToken) {
      return getAuthToken() === originalToken;
    }

    return false;
  } catch {
    return false;
  }
};

export const fetchApi = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options.headers) {
    Object.assign(headers, options.headers as Record<string, string>);
  }
  if (token && !('Authorization' in headers)) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, { ...options, headers });
    const contentType = response.headers.get('content-type') || '';
    const responseData = contentType.includes('application/json')
      ? await response.json()
      : { message: await response.text() };

    if (!response.ok) {
      const authReason = response.status === 401
        ? mapAuthFailureToLogoutReason((responseData as AuthFailureResponse)?.code)
        : undefined;

      if (authReason) {
        clearAuthState(authReason);
      }

      return {
        data: null,
        error: responseData?.message || `API request failed with status ${response.status}`,
        message: responseData?.message,
        status: response.status,
        code: responseData?.code,
      };
    }

    return {
      data: (responseData.data !== undefined ? responseData.data : responseData) as T,
      message: responseData?.message,
      status: response.status,
      code: responseData?.code,
    };
  } catch (error: any) {
    return { data: null, error: error.message || 'Network or parsing error' };
  }
};

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends LoginCredentials {
  firstName?: string;
  lastName?: string;
  roles?: string[];
}

export const login = async (credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> => {
  const response = await fetchApi<AuthResponse>(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });

  if (response.data?.token) {
    persistAuthState(response.data.token, response.data.session || null);
  }

  return response;
};

export const register = async (registerData: RegisterData): Promise<ApiResponse<{ message: string; user: IUser }>> => {
  return fetchApi<{ message: string; user: IUser }>(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    body: JSON.stringify(registerData),
  });
};

export const logout = async (options?: { server?: boolean; reason?: LogoutReason }): Promise<void> => {
  const token = getAuthToken();
  const existingReason = readLocalStorage(AUTH_LOGOUT_REASON_KEY);

  if (options?.server !== false && token) {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch {
      // Best effort; local logout still wins.
    }
  }

  if (!token && !options?.reason && existingReason) {
    dispatchAuthEvent({ type: 'logout', at: Date.now(), reason: existingReason });
    return;
  }

  clearAuthState(options?.reason);
};

export const getMe = async (): Promise<ApiResponse<AuthenticatedUserResponse>> => {
  const response = await fetchApi<AuthenticatedUserResponse>(`${API_BASE_URL}/auth/me`);
  if (response.data?.session) {
    persistSession(response.data.session);
  }
  return response;
};

export const heartbeat = async (): Promise<ApiResponse<SessionHeartbeatResponse>> => {
  const response = await fetchApi<SessionHeartbeatResponse>(`${API_BASE_URL}/auth/heartbeat`, {
    method: 'POST',
  });
  if (response.data?.session) {
    persistSession(response.data.session);
  }
  return response;
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<ApiResponse<{ message: string; logoutRequired?: boolean }>> => {
  return fetchApi<{ message: string; logoutRequired?: boolean }>(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
};

export const checkAndRefreshTokenIfNeeded = async (): Promise<boolean> => {
  return !!getAuthToken();
};

export {
  AUTH_ACTIVITY_KEY,
  AUTH_EVENT_KEY,
  AUTH_EVENT_NAME,
  AUTH_LOGOUT_REASON_KEY,
  AUTH_SESSION_KEY,
  AUTH_TOKEN_KEY,
};